import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import {
  grayColor,
  whiteColor,
} from "../../../../../assets/jss/material-dashboard-pro-react";
import AuthContext from "../../../../../connections/auth/AuthProvider";

// Import existing components
import { formatNumber } from "../../../../../services/utils/formatAndCompare";

// Import sub-components
import HierarchyControls from "./HierarchyControls";
import NodeVisibilityControls from "./NodeVisibilityControls";
import ColumnVisibilityControls from "./ColumnVisibilityControls";
import ColumnOrganizationToggle from "./ColumnOrganizationToggle";
import ScenarioTable from "./ScenarioTable";
import ComparisonSetupCard from "./ComparisonSetupCard";

// Import Material-UI components for comparison selector
import {
  Box,
  Select,
  MenuItem,
  IconButton,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";

// Import API calls
import {
  getComparisonFilterOptions,
  getComparisonData,
  getComparisons,
  updateComparison,
} from "../../../viewServices/calls/apiCalls";

// Import utilities
import { mapDataToColumns } from "./utils/columnUtils";

const styles = (clientColor) => ({
  priceRouteContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    fontSize: "11px", // Smaller base font
    maxHeight: "calc(100vh - 200px)", // Responsive height based on viewport
    overflow: "hidden",
  },
  // Node Controls (top section)
  nodeControls: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 12px",
    backgroundColor: grayColor[13],
    borderRadius: "4px",
    marginBottom: "8px",
    fontSize: "11px",
    flexShrink: 0,
  },
  nodeCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    "& input": {
      margin: 0,
      "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.6,
      },
    },
    "& label": {
      fontSize: "11px",
      cursor: "pointer",
      margin: 0,
      "&.disabled": {
        color: grayColor[6],
        cursor: "not-allowed",
      },
    },
  },
  // Controls container for node controls only
  controlsContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "8px",
    flexWrap: "wrap", // Allow wrapping on smaller screens
  },
  // Comparison selector box (compact, gray box style)
  comparisonSelectorBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 10px",
    backgroundColor: grayColor[13],
    borderRadius: "4px",
    border: `1px solid ${grayColor[10]}`,
    fontSize: "11px",
    flexShrink: 0,
  },
  // Scenario legend box (stacked vertically)
  scenarioLegendBox: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "6px 10px",
    backgroundColor: grayColor[13],
    borderRadius: "4px",
    border: `1px solid ${grayColor[10]}`,
    fontSize: "11px",
    flexShrink: 0,
  },
  scenarioLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  scenarioLabelBox: {
    backgroundColor: clientColor,
    color: whiteColor,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    borderRadius: "3px",
    fontSize: "11px",
    fontWeight: "bold",
    width: "120px",
    minWidth: "120px",
    maxWidth: "120px",
    overflow: "hidden",
  },
  scenarioName: {
    fontSize: "11px",
    color: grayColor[1],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px",
    cursor: "pointer",
    "&:hover": {
      color: grayColor[0],
    },
  },
  scenarioNameEditable: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  editIcon: {
    fontSize: "18px",
    color: grayColor[6],
    cursor: "pointer",
    padding: "2px",
    "&:hover": {
      color: clientColor,
      backgroundColor: grayColor[12],
      borderRadius: "2px",
    },
  },
  scenarioInput: {
    fontSize: "11px",
    "& input": {
      fontSize: "11px",
      padding: "2px 4px",
    },
  },
  comparisonSelect: {
    fontSize: "11px",
    padding: "2px 8px",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "3px",
    backgroundColor: whiteColor,
    minWidth: "180px",
    maxWidth: "250px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "& .MuiSelect-root": {
      padding: "4px 8px",
      fontSize: "11px",
    },
    "&:before": {
      borderBottom: "none",
    },
    "&:after": {
      borderBottom: "none",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottom: "none",
    },
  },
  comparisonCloseButton: {
    padding: "4px",
    fontSize: "14px",
    color: grayColor[4],
    "&:hover": {
      backgroundColor: grayColor[11],
      color: grayColor[1],
    },
  },
  // Top bar container for controls and toggle
  topBarContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "8px",
    gap: "12px",
  },
  // Right side container for toggle
  rightControls: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  // Main content layout
  mainContent: {
    display: "flex",
    gap: "8px",
    flex: 1,
    minHeight: 0,
    overflow: "auto", // Enable scrolling when content exceeds container
    maxHeight: "calc(100vh - 250px)", // Responsive max height
    // Custom scrollbar styling
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: grayColor[13],
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: grayColor[8],
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: grayColor[6],
      },
    },
  },
  // Hierarchy section (left)
  hierarchySection: {
    width: "140px", // Very compact
    flexShrink: 0,
    backgroundColor: grayColor[11],
    borderRadius: "4px",
    padding: "8px",
    border: `1px solid ${grayColor[8]}`,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  hierarchyTitle: {
    fontSize: "11px",
    fontWeight: "bold",
    color: grayColor[1],
    marginBottom: "4px",
  },
  selectorDropdown: {
    width: "100%",
    fontSize: "10px",
    padding: "4px",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "3px",
    backgroundColor: whiteColor,
    marginBottom: "8px",
  },
  hierarchyItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 6px",
    marginBottom: "4px",
    backgroundColor: clientColor,
    color: whiteColor,
    borderRadius: "3px",
    cursor: "move",
    fontSize: "10px",
    userSelect: "none",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
  },
  hierarchyItemText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  removeButton: {
    padding: "2px",
    cursor: "pointer",
    fontSize: "10px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      borderRadius: "2px",
    },
  },
  dropZone: {
    minHeight: "30px",
    border: `2px dashed ${grayColor[8]}`,
    borderRadius: "3px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: grayColor[4],
    fontSize: "9px",
    backgroundColor: grayColor[13],
    textAlign: "center",
    padding: "4px",
    "&.dragOver": {
      borderColor: clientColor,
      backgroundColor: `${clientColor}20`,
      color: clientColor,
    },
  },
  // PnL section (right)
  pnlSection: {
    flex: 1,
    display: "flex",
    minWidth: 0,
    overflow: "auto", // Enable horizontal scrolling for wide tables
    maxWidth: "100%",
    // Custom scrollbar styling
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: grayColor[13],
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: grayColor[8],
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: grayColor[6],
      },
    },
  },
  scenarioColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "4px",
    backgroundColor: whiteColor,
    overflow: "auto", // Enable scrolling within individual scenario columns
    minWidth: "400px", // Increased minimum width for full column names
    maxHeight: "100%",
  },
  scenarioHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 8px",
    backgroundColor: clientColor,
    color: whiteColor,
    fontSize: "11px",
    fontWeight: "bold",
    flexShrink: 0,
    borderTopRightRadius: "4px", // Add border radius for top-right corner
    borderTopLeftRadius: "4px", // Add border radius for top-left corner too
    border: `1px solid ${grayColor[8]}`,
    borderBottom: "none", // Connect to table below
  },
  scenarioClose: {
    cursor: "pointer",
    fontSize: "12px",
    padding: "4px",
    borderRadius: "2px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      opacity: 0.8,
    },
  },
  placeholderText: {
    color: grayColor[6],
    fontStyle: "italic",
    fontSize: "9px",
  },
  scenarioContent: {
    flex: 1,
    overflow: "auto",
    fontSize: "10px",
  },
  // PnL table styling (very compact)
  pnlTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "9px", // Very small for 10 columns
  },
  headerRow: {
    backgroundColor: grayColor[12],
    position: "sticky",
    top: "20px", // Position below the node header row
    zIndex: 2,
  },
  nodeHeaderRow: {
    backgroundColor: grayColor[11],
    position: "sticky",
    top: 0,
    zIndex: 3,
  },
  nodeHeaderCell: {
    padding: "4px 2px",
    border: `1px solid ${grayColor[8]}`,
    fontSize: "9px",
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: grayColor[11],
    color: grayColor[6], // Changed text color to black
  },
  headerCell: {
    padding: "4px 2px", // Very tight padding
    border: `1px solid ${grayColor[8]}`,
    fontSize: "8px",
    fontWeight: "bold",
    textAlign: "center",
    maxWidth: "50px", // Slightly wider for wrapped text
    minWidth: "40px", // Minimum width
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal", // Allow text wrapping
    lineHeight: "1.1", // Tighter line height for wrapped text
    verticalAlign: "top", // Align to top for multi-line headers
  },
  hierarchyHeaderCell: {
    position: "sticky",
    left: 0,
    backgroundColor: grayColor[12],
    zIndex: 3,
    textAlign: "left",
    minWidth: "80px",
    maxWidth: "120px",
    border: "none", // Remove border from dimensions column header
  },
  dataRow: {
    "&:hover": {
      backgroundColor: grayColor[13],
    },
  },
  hierarchyCell: {
    position: "sticky",
    left: 0,
    backgroundColor: whiteColor,
    zIndex: 1,
    padding: "3px 6px",
    border: `1px solid ${grayColor[8]}`,
    display: "flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "9px",
    minWidth: "80px",
    maxWidth: "120px",
    overflow: "hidden",
    "&:hover": {
      backgroundColor: grayColor[13],
    },
  },
  valueCell: {
    padding: "3px 2px",
    border: `1px solid ${grayColor[8]}`,
    textAlign: "right",
    fontSize: "8px",
    maxWidth: "50px", // Match header cell width
    minWidth: "40px", // Match header cell width
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
  },
  expandIcon: {
    fontSize: "8px",
    width: "10px",
    color: grayColor[6],
  },
  levelIndent: {
    marginLeft: (props) => `${props.level * 8}px`,
  },
  // PnL level styling (from original Pnl component)
  plTotal: {
    fontWeight: "bold",
    backgroundColor: grayColor[11],
  },
  plSubTotal: {
    fontWeight: 500,
    backgroundColor: grayColor[13],
  },
  plLevel3: {
    fontWeight: 400,
  },
  placeholderContent: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    backgroundColor: whiteColor,
    borderRadius: "8px",
    border: `1px solid ${grayColor[10]}`,
    textAlign: "center",
    padding: "40px",
    width: "100%",
    boxSizing: "border-box",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  skeletonTable: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 0.3,
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    gap: "4px",
  },
  skeletonHeaderRow: {
    display: "flex",
    width: "100%",
    height: "60px",
    gap: "4px",
  },
  skeletonMainContent: {
    display: "flex",
    width: "100%",
    flex: 1,
    gap: "4px",
  },
  skeletonHierarchyColumn: {
    backgroundColor: grayColor[10],
    width: "120px",
    borderRadius: "4px",
  },
  skeletonHeaderBlock: {
    backgroundColor: grayColor[9],
    flex: 1,
    borderRadius: "4px",
  },
  skeletonLeftSection: {
    backgroundColor: grayColor[11],
    flex: 1,
    borderRadius: "4px",
  },
  skeletonRightSection: {
    backgroundColor: grayColor[11],
    flex: 1,
    borderRadius: "4px",
  },
  placeholderOverlay: {
    position: "relative",
    zIndex: 2,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    borderRadius: "8px",
  },
  hierarchyLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 5,
    borderRadius: "8px",
  },
  tableLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    zIndex: 10,
    borderRadius: "8px",
    fontSize: "14px",
    color: grayColor[1],
    fontWeight: "500",
  },
  placeholderIcon: {
    fontSize: "48px",
    color: grayColor[8],
    marginBottom: "20px",
  },
  placeholderTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: grayColor[2],
    marginBottom: "12px",
  },
  placeholderDescription: {
    fontSize: "14px",
    color: grayColor[4],
    lineHeight: 1.6,
    maxWidth: "400px",
  },
  placeholderSteps: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  placeholderStep: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "0.875rem",
    fontWeight: 400,
    color: "rgba(0, 0, 0, 0.45)",
  },
  stepIcon: {
    fontSize: "12px",
    color: grayColor[7],
  },
});

const ComparativeTableView = ({
  selectedComparison,
  // eslint-disable-next-line no-unused-vars
  onComparisonSelect,
  selectedFilters = {},
  scenarios = [],
}) => {
  const { userDetails } = useContext(AuthContext);
  const client_styles = () => ({
    ...styles(userDetails.implementation_color),
  });
  const useStyles = makeStyles(client_styles);
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Client color for theming
  const clientColor = userDetails.implementation_color;

  // State management
  const [hierarchyOrder, setHierarchyOrder] = useState([]);
  const [availableDimensions, setAvailableDimensions] = useState({});
  const [nodeStructure, setNodeStructure] = useState({});
  const [priceRouteData, setPriceRouteData] = useState(null);
  const [hierarchicalData, setHierarchicalData] = useState([]); // Store hierarchical data as array
  const [isLoadingDimensions, setIsLoadingDimensions] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false); // Track initial comparison load
  const [visibleNodes, setVisibleNodes] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({}); // New state for column visibility
  const [drillDownsOpen, setDrillDownsOpen] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [columnOrganization, setColumnOrganization] = useState("elo-first"); // 'scenario-first' | 'elo-first'

  // New metadata state
  const [metadata, setMetadata] = useState(null);

  // State for available comparisons
  const [availableComparisons, setAvailableComparisons] = useState([]);

  // State for editing scenario labels
  const [editingScenario, setEditingScenario] = useState(null); // 'A' or 'B' or null
  const [editLabelValue, setEditLabelValue] = useState("");
  // Local state for labels to avoid triggering comparison change
  const [localScenarioALabel, setLocalScenarioALabel] = useState(null);
  const [localScenarioBLabel, setLocalScenarioBLabel] = useState(null);

  // Fetch available comparisons on mount
  useEffect(() => {
    const fetchAvailableComparisons = async () => {
      try {
        const response = await getComparisons();
        if (response.status === "success") {
          setAvailableComparisons(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching available comparisons:", error);
        setAvailableComparisons([]);
      }
    };

    fetchAvailableComparisons();
  }, []);

  // Expose comparison list refresh for ComparisonSetupCard to sync changes
  useEffect(() => {
    window.priceroute_refreshComparisons = async () => {
      try {
        const response = await getComparisons();
        if (response.status === "success") {
          setAvailableComparisons(response.data || []);
          console.log("PriceRoute: Comparison list refreshed via callback");
        }
      } catch (error) {
        console.error("PriceRoute: Failed to refresh comparisons:", error);
      }
    };

    return () => {
      delete window.priceroute_refreshComparisons;
    };
  }, []);

  // Initialize metadata and visible columns when comparison data is loaded
  // Metadata now comes merged with data response, not separate API call
  const initializeMetadataFromData = useCallback((responseMetadata) => {
    if (!responseMetadata || !responseMetadata.nodes) return;

    setMetadata(responseMetadata);
    setNodeStructure(responseMetadata.nodes || {});

    // Initialize visibleNodes
    const initialVisibleNodes = {};
    Object.keys(responseMetadata.nodes).forEach((nodeKey) => {
      initialVisibleNodes[nodeKey] = true;
    });
    setVisibleNodes(initialVisibleNodes);

    // Initialize visibleColumns - hide rate columns by default
    const initialVisibleColumns = {};
    Object.keys(responseMetadata.nodes).forEach((nodeKey) => {
      const nodeMetrics = responseMetadata.nodes[nodeKey].columns || [];
      nodeMetrics.forEach((metric) => {
        const isRateColumn =
          metric.column_name &&
          metric.column_name.toLowerCase().includes("rate");

        if (metric.calculation_type === "delta_rate") {
          const deltaKey = `delta_${nodeKey}_${metric.column_id}`;
          initialVisibleColumns[deltaKey] = !isRateColumn;
        } else {
          const asIsKey = `asis_${nodeKey}_${metric.column_id}`;
          const toBeKey = `tobe_${nodeKey}_${metric.column_id}`;
          initialVisibleColumns[asIsKey] = !isRateColumn;
          initialVisibleColumns[toBeKey] = !isRateColumn;
        }
      });
    });
    setVisibleColumns(initialVisibleColumns);
  }, []);

  // Fetch available dimensions when comparison is selected
  useEffect(() => {
    const fetchDimensions = async () => {
      if (!selectedComparison) {
        setAvailableDimensions({});
        setIsLoadingDimensions(false);
        return;
      }

      setIsLoadingDimensions(true);
      try {
        console.log(
          "Fetching dimensions for comparison:",
          selectedComparison.comparison_id,
        );
        const response = await getComparisonFilterOptions(
          selectedComparison.comparison_id,
        );
        if (response.status === "success") {
          console.log("Dimensions received:", response.data);
          setAvailableDimensions(response.data);

          // Auto-set 'brand' as default hierarchy if no hierarchy is configured
          // Use functional setState to check current state value (avoid stale closure)
          if (response.data.brand) {
            setHierarchyOrder((currentHierarchy) => {
              if (currentHierarchy.length === 0) {
                console.log("Auto-setting brand as default hierarchy");
                return ["brand"];
              }
              return currentHierarchy;
            });
          }
        } else {
          console.error("Failed to fetch dimensions:", response);
          setAvailableDimensions({});
        }
      } catch (error) {
        console.error("Error fetching dimensions:", error);
        // Set empty state on error - no fallback to constants
        setAvailableDimensions({});
      } finally {
        setIsLoadingDimensions(false);
      }
    };

    fetchDimensions();
  }, [selectedComparison]); // Don't add hierarchyOrder to dependencies

  // Add ref to track current request and prevent race conditions
  const currentRequestRef = useRef(null);

  // Fetch price route data when comparison changes (don't require hierarchy for basic display)
  useEffect(() => {
    const fetchPriceRouteData = async () => {
      // Only fetch data when we have both a comparison AND hierarchy configured
      if (
        !selectedComparison ||
        !hierarchyOrder ||
        hierarchyOrder.length === 0
      ) {
        setPriceRouteData(null);
        setHierarchicalData([]);
        setIsLoadingData(false);
        return;
      }

      // Cancel any existing request
      if (currentRequestRef.current) {
        console.log("Cancelling previous request");
        currentRequestRef.current.cancelled = true;
      }

      // Create new request tracker
      const requestId = Date.now();
      currentRequestRef.current = { id: requestId, cancelled: false };

      setIsLoadingData(true);
      try {
        console.log(
          `[Request ${requestId}] Fetching data for comparison:`,
          selectedComparison,
          "with hierarchy:",
          hierarchyOrder,
          "with filters:",
          selectedFilters,
        );

        // Load complete hierarchical structure using the new comparison data endpoint
        const response = await getComparisonData(
          selectedComparison,
          selectedFilters, // Pass the applied filters from ComparativeTab
          hierarchyOrder, // Pass the current hierarchy order
        ); // Check if this request was cancelled
        if (
          currentRequestRef.current?.cancelled ||
          currentRequestRef.current?.id !== requestId
        ) {
          console.log(
            `[Request ${requestId}] Request was cancelled, ignoring response`,
          );
          return;
        }

        if (response.status === "success") {
          console.log(
            `[Request ${requestId}] Hierarchical data received:`,
            response.data,
          );
          console.log("Sample hierarchy item:", response.data[0]);
          console.log("Total hierarchy items:", response.data.length);

          // Store complete hierarchical data
          console.log(
            "Setting hierarchicalData state with:",
            response.data.length,
            "items",
          );
          setHierarchicalData(response.data);
          console.log("hierarchicalData state has been set");

          // Initialize metadata from merged response (optimization)
          if (response.metadata) {
            console.log(
              "Metadata received in data response (optimized):",
              response.metadata,
            );
            initializeMetadataFromData(response.metadata);
          }

          // Extract Elo structure from response if available
          if (response.elo_structure) {
            console.log("Elo structure received:", response.elo_structure);
            setNodeStructure(response.elo_structure);
          } else {
            // Fallback to default Elo structure
            console.log("Using fallback Elo structure");
            setNodeStructure({
              elo1: {
                name: "Elo 1 - Industria",
              },
              elo2: {
                name: "Elo 2 - Indireto & Direto",
              },
              elo3: { name: "Elo 3 - POS" },
            });
          }

          // Also keep the old format for compatibility
          setPriceRouteData({
            rows: response.data,
          });

          // Initial load complete
          setIsInitialLoad(false);
        } else {
          console.error(`[Request ${requestId}] API returned error:`, response);
          setHierarchicalData([]);
          setPriceRouteData(null);
        }
      } catch (error) {
        // Check if this request was cancelled
        if (
          currentRequestRef.current?.cancelled ||
          currentRequestRef.current?.id !== requestId
        ) {
          console.log(
            `[Request ${requestId}] Request was cancelled, ignoring error`,
          );
          return;
        }
        console.error(
          `[Request ${requestId}] Error fetching price route data:`,
          error,
        );
        setPriceRouteData(null);
        setHierarchicalData([]);
      } finally {
        // Only update loading state if this request wasn't cancelled
        if (
          currentRequestRef.current?.id === requestId &&
          !currentRequestRef.current?.cancelled
        ) {
          setIsLoadingData(false);
        }
      }
    };

    // Debounce the fetch to avoid rapid successive calls
    const timeoutId = setTimeout(fetchPriceRouteData, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [selectedComparison, hierarchyOrder, selectedFilters]); // Triggers on comparison, hierarchy, OR filter changes

  // Clear ALL state when comparison changes (treat as fresh view)
  useEffect(() => {
    // Start initial load when comparison changes - but don't set isLoadingDimensions here
    // Let the fetchDimensions useEffect handle that to avoid race condition
    if (selectedComparison) {
      setIsInitialLoad(true);
    } else {
      setIsInitialLoad(false);
    }

    setDrillDownsOpen({});
    setHierarchicalData([]);
    setPriceRouteData(null);
    setHierarchyOrder([]); // Reset hierarchy - fresh start
    setAvailableDimensions({});

    // Debug: Log what labels we're getting
    console.log("selectedComparison changed:", selectedComparison);
    console.log("scenario_a_label:", selectedComparison?.scenario_a_label);
    console.log("scenario_b_label:", selectedComparison?.scenario_b_label);

    // Reset local labels when comparison changes
    setLocalScenarioALabel(selectedComparison?.scenario_a_label || null);
    setLocalScenarioBLabel(selectedComparison?.scenario_b_label || null);
  }, [selectedComparison]);

  // Expose loading state setter for ComparisonSetupCard to prevent blink
  useEffect(() => {
    window.priceroute_setLoadingState = () => {
      setIsInitialLoad(true);
      setIsLoadingDimensions(true);
    };

    return () => {
      delete window.priceroute_setLoadingState;
    };
  }, []);

  // Clear drill-down state when hierarchy order changes (but keep data fetching in other useEffect)
  useEffect(() => {
    setDrillDownsOpen({});
  }, [hierarchyOrder]);

  // Get available dimensions that are not in hierarchy
  const dimensionsNotInHierarchy = useMemo(() => {
    return Object.keys(availableDimensions).filter(
      (dim) => dim !== "pnl_flags" && !hierarchyOrder.includes(dim),
    );
  }, [availableDimensions, hierarchyOrder]);

  // Transform available dimensions for HierarchyControls component
  const dimensionDisplayNames = useMemo(() => {
    const displayNames = {};
    Object.keys(availableDimensions).forEach((key) => {
      // Skip pnl_flags - it's a filter parameter, not a hierarchy dimension
      if (key === "pnl_flags") {
        return;
      }

      // Handle the new backend response structure with name and values
      const dimensionData = availableDimensions[key];
      if (
        dimensionData &&
        typeof dimensionData === "object" &&
        dimensionData.name
      ) {
        // Backend provides structured data with name and values - use simple string
        displayNames[key] = dimensionData.name;
      } else {
        // If backend provides simple string, use it directly
        displayNames[key] =
          dimensionData || key.charAt(0).toUpperCase() + key.slice(1);
      }
    });
    return displayNames;
  }, [availableDimensions]);

  // Clear drill-downs when hierarchy order changes
  useEffect(() => {
    if (priceRouteData?.rows) {
      const expandedState = {};
      // Auto-expand all items for now
      priceRouteData.rows.forEach((item, index) => {
        expandedState[`item_${index}`] = true;
      });
      setDrillDownsOpen(expandedState);
    }
  }, [priceRouteData]);

  // Handle adding dimension to hierarchy
  const handleAddDimension = useCallback(
    (dimension) => {
      if (dimension && !hierarchyOrder.includes(dimension)) {
        // If adding the first dimension, set loading immediately to prevent table blink
        if (hierarchyOrder.length === 0) {
          setIsLoadingData(true);
        }
        setHierarchyOrder((prev) => [...prev, dimension]);
      }
    },
    [hierarchyOrder],
  );

  // Handle removing dimension from hierarchy
  const handleRemoveDimension = useCallback((dimension) => {
    setHierarchyOrder((prev) => prev.filter((dim) => dim !== dimension));
  }, []);

  // Handle clearing all dimensions from hierarchy
  const handleClearAll = useCallback(() => {
    setHierarchyOrder([]);
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((e, dimension, index) => {
    setDraggedItem({ dimension, index });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", dimension);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
  }, []);

  // Handle drag over
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault();
      if (draggedItem && draggedItem.index !== dropIndex) {
        const newOrder = [...hierarchyOrder];
        const [draggedDimension] = newOrder.splice(draggedItem.index, 1);
        newOrder.splice(dropIndex, 0, draggedDimension);
        setHierarchyOrder(newOrder);
      }
      setDraggedItem(null);
    },
    [draggedItem, hierarchyOrder],
  );

  // Handle node visibility toggle with column synchronization
  const handleNodeVisibilityChange = useCallback(
    (nodeKey, isVisible) => {
      // Update node visibility
      setVisibleNodes((prev) => ({
        ...prev,
        [nodeKey]: isVisible,
      }));

      // Synchronize column visibility when node visibility changes
      if (metadata && metadata.nodes && metadata.nodes[nodeKey]) {
        setVisibleColumns((prevColumns) => {
          const updatedColumns = { ...prevColumns };
          const nodeColumns = metadata.nodes[nodeKey].columns || [];

          nodeColumns.forEach((column) => {
            // Handle delta_rate columns differently - they only have delta values
            if (column.calculation_type === "delta_rate") {
              const deltaKey = `delta_${nodeKey}_${column.column_id}`;
              updatedColumns[deltaKey] = isVisible;
            } else {
              // Regular columns have both asis and tobe values
              const asIsKey = `asis_${nodeKey}_${column.column_id}`;
              const toBeKey = `tobe_${nodeKey}_${column.column_id}`;
              updatedColumns[asIsKey] = isVisible;
              updatedColumns[toBeKey] = isVisible;
            }
          });

          return updatedColumns;
        });
      }
    },
    [metadata],
  );

  // Handle column visibility change with bidirectional node sync
  const handleColumnVisibilityChange = useCallback(
    (columnKey, isVisible) => {
      setVisibleColumns((prev) => {
        const updatedColumns = {
          ...prev,
          [columnKey]: isVisible,
        };

        // Check if we need to update node visibility based on column changes
        if (metadata && metadata.nodes) {
          // Extract node key from column key (format: "asis_nodeKey_metricId" or "delta_nodeKey_metricId")
          const parts = columnKey.split("_");
          if (parts.length >= 3) {
            const nodeKey = parts[1]; // Extract nodeKey from column key

            if (metadata.nodes[nodeKey]) {
              const nodeColumns = metadata.nodes[nodeKey].columns || [];

              // Check visibility state of all columns for this node
              let allColumnsInvisible = true;
              let anyColumnVisible = false;

              nodeColumns.forEach((column) => {
                if (column.calculation_type === "delta_rate") {
                  const deltaKey = `delta_${nodeKey}_${column.column_id}`;
                  if (updatedColumns[deltaKey]) {
                    allColumnsInvisible = false;
                    anyColumnVisible = true;
                  }
                } else {
                  const asIsKey = `asis_${nodeKey}_${column.column_id}`;
                  const toBeKey = `tobe_${nodeKey}_${column.column_id}`;
                  if (updatedColumns[asIsKey] || updatedColumns[toBeKey]) {
                    allColumnsInvisible = false;
                    anyColumnVisible = true;
                  }
                }
              });

              // Update node visibility based on column visibility
              setVisibleNodes((prevNodes) => {
                if (allColumnsInvisible && prevNodes[nodeKey]) {
                  // All columns are invisible, uncheck the node
                  return {
                    ...prevNodes,
                    [nodeKey]: false,
                  };
                } else if (anyColumnVisible && !prevNodes[nodeKey]) {
                  // Some columns are visible, check the node
                  return {
                    ...prevNodes,
                    [nodeKey]: true,
                  };
                }
                return prevNodes; // No change needed
              });
            }
          }
        }

        return updatedColumns;
      });
    },
    [metadata],
  );

  // Handle column organization toggle
  const handleColumnOrganizationToggle = useCallback((newOrganization) => {
    console.log("Column organization changed to:", newOrganization);
    setColumnOrganization(newOrganization);
  }, []);

  // Handle comparison change from selector
  const handleComparisonSelectorChange = useCallback(
    (event) => {
      const comparisonId = event.target.value;
      const comparison = availableComparisons.find(
        (c) => c.comparison_id === comparisonId,
      );
      if (comparison && onComparisonSelect) {
        // Set loading state immediately to prevent blink
        setIsInitialLoad(true);
        setIsLoadingDimensions(true);
        onComparisonSelect(comparison);
      }
    },
    [availableComparisons, onComparisonSelect],
  );

  // Handle clearing comparison (show setup card again)
  const handleClearComparison = useCallback(() => {
    if (onComparisonSelect) {
      onComparisonSelect(null);
    }
  }, [onComparisonSelect]);

  // Handle drill-down toggle - now purely frontend (no API calls)
  const handleDrillDownClick = useCallback((itemKey) => {
    // Simply toggle the drill-down state
    setDrillDownsOpen((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  }, []);

  // Handle expand all drill-downs
  const handleExpandAll = useCallback(() => {
    // Get all available item keys from hierarchical data
    const allKeys = hierarchicalData.reduce((keys, item) => {
      if (item.is_parent && item.path_key !== "Total") {
        keys.push(item.path_key);
      }
      return keys;
    }, []);

    // Set all keys to expanded (true)
    const expandedState = allKeys.reduce((state, key) => {
      state[key] = true;
      return state;
    }, {});

    setDrillDownsOpen(expandedState);
  }, [hierarchicalData]);

  // Handle collapse all drill-downs
  const handleCollapseAll = useCallback(() => {
    // Set all drill-downs to collapsed (empty object)
    setDrillDownsOpen({});
  }, []);

  // Handle editing scenario label
  const handleEditScenarioLabel = useCallback(
    (scenario) => {
      const currentLabel =
        scenario === "A"
          ? localScenarioALabel ||
            t("comparative.comparison.scenario_a_default")
          : localScenarioBLabel ||
            t("comparative.comparison.scenario_b_default");

      setEditingScenario(scenario);
      setEditLabelValue(currentLabel);
    },
    [localScenarioALabel, localScenarioBLabel, t],
  );

  // Handle save scenario label
  const handleSaveScenarioLabel = useCallback(async () => {
    if (!editingScenario || !selectedComparison) return;

    const newLabel = editLabelValue.trim() || null;
    const scenarioKey = editingScenario;

    // Update local label state immediately (no parent update = no refetch)
    if (editingScenario === "A") {
      setLocalScenarioALabel(newLabel);
    } else {
      setLocalScenarioBLabel(newLabel);
    }

    // Reset editing state
    setEditingScenario(null);
    setEditLabelValue("");

    // Send update to backend and refresh comparisons list
    try {
      const labels = {};
      if (scenarioKey === "A") {
        labels.scenario_a_label = newLabel || "";
      } else {
        labels.scenario_b_label = newLabel || "";
      }

      await updateComparison(selectedComparison.comparison_id, labels);

      // Refresh comparisons list to get updated labels
      const response = await getComparisons();
      if (response.status === "success") {
        setAvailableComparisons(response.data || []);
        console.log("Comparisons list refreshed with updated labels");
      }
    } catch (error) {
      console.error("Failed to update scenario label:", error);
    }
  }, [editingScenario, editLabelValue, selectedComparison]);

  // Handle cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingScenario(null);
    setEditLabelValue("");
  }, []);

  // Handle Enter key to save
  const handleLabelKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSaveScenarioLabel();
      } else if (e.key === "Escape") {
        handleCancelEdit();
      }
    },
    [handleSaveScenarioLabel, handleCancelEdit],
  );

  // Check if item should be visible based on parent expansion
  const shouldItemBeVisible = useCallback(
    (item, allItems) => {
      // Root level items are always visible
      if (!item.parent_path) return true;

      // Check if parent is expanded
      if (!drillDownsOpen[item.parent_path]) return false;

      // Recursively check parent visibility
      const parentItem = allItems.find((i) => i.path_key === item.parent_path);
      if (!parentItem) return true;

      return shouldItemBeVisible(parentItem, allItems);
    },
    [drillDownsOpen],
  );

  // Note: Column generation is handled directly in ScenarioTable component
  // This was previously used for column generation but is now handled differently

  // Memoize scenario data generation - now processes complete hierarchical structure
  const getScenarioData = useCallback(
    (scenarioIndex) => {
      console.log("getScenarioData called with:", {
        scenarioIndex,
        selectedComparison: !!selectedComparison,
        hierarchyOrder,
        hierarchicalDataLength: hierarchicalData?.length || 0,
      });

      // Return data for a specific scenario (A or B)
      // Only process data when we have comparison, hierarchy configured, and actual data
      if (
        !selectedComparison ||
        !hierarchyOrder ||
        hierarchyOrder.length === 0 ||
        !hierarchicalData ||
        hierarchicalData.length === 0
      ) {
        console.log(
          "getScenarioData: Missing required data, returning empty array",
        );
        return [];
      }

      try {
        const scenarioType = scenarioIndex === 0 ? "A" : "B";
        console.log(
          "Processing scenario type:",
          scenarioType,
          "for scenarioIndex:",
          scenarioIndex,
        );

        // Build tree structure from complete hierarchical data
        const treeStructure = buildTreeFromHierarchicalData(hierarchicalData);
        console.log("Tree structure built:", treeStructure);

        // Convert tree to flat array with expand/collapse states
        const flatArray = convertTreeToFlatArray(
          treeStructure,
          0,
          null,
          scenarioType,
        );
        console.log(
          `Flat array generated for ${scenarioType}:`,
          flatArray.length,
          "items",
        );
        console.log(`Sample ${scenarioType} data:`, flatArray[0]?.values);
        console.log(
          `Sample ${scenarioType} rawData structure:`,
          flatArray[0]?.rawData
            ? Object.keys(flatArray[0].rawData)
            : "no rawData",
        );
        console.log(
          `Sample ${scenarioType} rawData values:`,
          flatArray[0]?.rawData?.values,
        );

        return flatArray;
      } catch (error) {
        console.error("Error processing hierarchical data:", error);
        // Instead of falling back to mock data, return empty array
        return [];
      }
    },
    [
      selectedComparison,
      hierarchyOrder,
      hierarchicalData,
      drillDownsOpen,
      visibleColumns,
      columnOrganization,
    ],
  );

  // Build tree structure from complete hierarchical data
  const buildTreeFromHierarchicalData = (data) => {
    console.log("Building hierarchical pivot tree from data:", data);
    console.log("Data length:", data.length);
    console.log("Current hierarchy order:", hierarchyOrder);

    if (!data || data.length === 0) {
      console.log("No data provided to buildTreeFromHierarchicalData");
      return {};
    }

    console.log("Sample data items:");
    data.slice(0, 5).forEach((item, index) => {
      console.log(`Item ${index}:`, {
        path_key: item.path_key,
        level: item.level,
        hierarchy: item.hierarchy,
        is_parent: item.is_parent,
      });
    });

    // Build a map to store all nodes by their path_key for easy lookup
    const nodeMap = {};
    const tree = {};

    // First pass: create all nodes
    data.forEach((item) => {
      const pathKey = item.path_key || "Total";
      nodeMap[pathKey] = {
        ...item,
        children: {},
        displayName: getDisplayNameFromHierarchy(item.hierarchy, item.level),
      };
    });

    console.log("Created node map with keys:", Object.keys(nodeMap));

    // Second pass: build tree structure using parent_path relationships
    data.forEach((item) => {
      const pathKey = item.path_key || "Total";
      const parentPath = item.parent_path;

      if (parentPath && nodeMap[parentPath]) {
        // This is a child node, add it to its parent
        nodeMap[parentPath].children[pathKey] = nodeMap[pathKey];
        console.log(`Added child ${pathKey} to parent ${parentPath}`);
      } else if (!parentPath || parentPath === null) {
        // This is a root node
        tree[pathKey] = nodeMap[pathKey];
        console.log(`Added root node ${pathKey}`);
      } else {
        console.warn(
          `Parent not found for ${pathKey}, parent_path: ${parentPath}`,
        );
        // Fallback: add as root node
        tree[pathKey] = nodeMap[pathKey];
      }
    });

    console.log("Built hierarchical tree with root keys:", Object.keys(tree));
    console.log("Sample tree structure:", tree);
    return tree;
  };

  // Helper function to get display name from hierarchy
  const getDisplayNameFromHierarchy = (hierarchy) => {
    if (!hierarchy || Object.keys(hierarchy).length === 0) {
      return "Total";
    }

    // Get the values in hierarchy order
    if (hierarchyOrder && hierarchyOrder.length > 0) {
      const values = hierarchyOrder
        .map((dim) => hierarchy[dim])
        .filter((val) => val && val !== "");
      if (values.length > 0) {
        return values[values.length - 1]; // Return the last (most specific) value
      }
    }

    // Fallback: use any available value
    const values = Object.values(hierarchy).filter((val) => val && val !== "");
    return values.length > 0 ? values[values.length - 1] : "Total";
  };

  // Convert tree structure to flat array respecting expand/collapse states
  const convertTreeToFlatArray = (
    tree,
    level = 0,
    parentKey = null,
    scenarioType = "asis",
  ) => {
    const result = [];

    Object.keys(tree).forEach((itemKey) => {
      const item = tree[itemKey];
      const hasChildren = Object.keys(item.children || {}).length > 0;
      const pathKey = item.path_key || itemKey;
      const isExpanded = drillDownsOpen[pathKey] || false;

      // Use the displayName we set during tree building
      const displayName = item.displayName || "Total";

      // Create values object using column mapping utility
      const values = mapDataToColumns(item, visibleColumns, columnOrganization);

      console.log(`Processing ${displayName}:`, {
        itemKey,
        pathKey,
        hasChildren,
        isExpanded,
        drillDownState: drillDownsOpen[pathKey],
        itemKeys: Object.keys(item),
        hasValues: !!item.values,
        valuesKeys: item.values ? Object.keys(item.values) : null,
        visibleColumnsCount: visibleColumns.length,
        finalValues: values,
        finalValuesKeys: Object.keys(values),
      });

      // Create node data
      const nodeData = {
        key: `${pathKey}_${level}`, // Use pathKey for consistency
        name: displayName,
        level: level,
        is_parent: hasChildren,
        meta_name: pathKey,
        parent_path: item.parent_path, // Add parent_path for visibility checking
        parent: parentKey,
        values: values,
        hierarchyPath: Object.values(item.hierarchy || {}),
        rawData: item,
        isExpanded: isExpanded,
        itemKey: pathKey, // Use pathKey for consistency
      };

      console.log(`Converting node ${displayName} at level ${level}:`, {
        itemKey: pathKey,
        displayName,
        hasChildren,
        isExpanded,
        valuesKeys: Object.keys(values),
      });

      result.push(nodeData);

      // Add children if expanded
      if (hasChildren && isExpanded) {
        console.log(`Expanding children for ${displayName}`);
        const childrenData = convertTreeToFlatArray(
          item.children,
          level + 1,
          pathKey, // Use pathKey for parent reference
          scenarioType,
        );
        result.push(...childrenData);
      }
    });

    console.log(
      `convertTreeToFlatArray at level ${level} returning ${result.length} items`,
    );
    return result;
  };

  // Render scenario column

  // Calculate if we're in an initial loading state
  const isFullyLoading =
    isInitialLoad || (selectedComparison && isLoadingDimensions);

  return (
    <div className={classes.priceRouteContainer}>
      {/* Top Bar Container - Only show when not in initial loading */}
      {!isFullyLoading && (
        <div className={classes.topBarContainer}>
          <div className={classes.controlsContainer}>
            {/* Compact Comparison Selector + Scenario Legend */}
            {selectedComparison && (
              <>
                <Box className={classes.comparisonSelectorBox}>
                  <Select
                    value={selectedComparison.comparison_id}
                    onChange={handleComparisonSelectorChange}
                    className={classes.comparisonSelect}
                    disableUnderline
                  >
                    {availableComparisons.map((comparison) => (
                      <MenuItem
                        key={comparison.comparison_id}
                        value={comparison.comparison_id}
                      >
                        {comparison.comparison_name}
                      </MenuItem>
                    ))}
                  </Select>
                  <IconButton
                    size="small"
                    onClick={handleClearComparison}
                    className={classes.comparisonCloseButton}
                    title={t("comparative.comparison.clear_tooltip")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Scenario Legend - stacked vertically with editable labels + scenario names */}
                <Box className={classes.scenarioLegendBox}>
                  {/* Scenario A */}
                  <Box className={classes.scenarioLegendItem}>
                    {editingScenario === "A" ? (
                      <Box className={classes.scenarioLabelBox}>
                        <TextField
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={handleLabelKeyDown}
                          onBlur={handleSaveScenarioLabel}
                          autoFocus
                          className={classes.scenarioInput}
                          size="small"
                          variant="standard"
                          autoComplete="off"
                          InputProps={{
                            style: {
                              fontSize: "11px",
                              color: whiteColor,
                            },
                            disableUnderline: true,
                          }}
                          inputProps={{
                            style: {
                              color: whiteColor,
                              padding: "2px 4px",
                            },
                            autoComplete: "off",
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <Box className={classes.scenarioLabelBox}>
                          <Box
                            title={
                              localScenarioALabel ||
                              t("comparative.comparison.scenario_a_default")
                            }
                            style={{
                              color: whiteColor,
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {localScenarioALabel ||
                              t("comparative.comparison.scenario_a_default")}
                          </Box>
                          <EditIcon
                            className={classes.editIcon}
                            onClick={() => handleEditScenarioLabel("A")}
                            titleAccess={t(
                              "comparative.comparison.edit_label_tooltip",
                            )}
                            style={{ color: whiteColor, flexShrink: 0 }}
                          />
                        </Box>
                        <Box
                          title={selectedComparison.scenario_a?.scenario_name}
                          style={{
                            fontSize: "11px",
                            color: "#000000",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "150px",
                          }}
                        >
                          {selectedComparison.scenario_a?.scenario_name}
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* Scenario B */}
                  <Box className={classes.scenarioLegendItem}>
                    {editingScenario === "B" ? (
                      <Box className={classes.scenarioLabelBox}>
                        <TextField
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={handleLabelKeyDown}
                          onBlur={handleSaveScenarioLabel}
                          autoFocus
                          className={classes.scenarioInput}
                          size="small"
                          variant="standard"
                          autoComplete="off"
                          InputProps={{
                            style: {
                              fontSize: "11px",
                              color: whiteColor,
                            },
                            disableUnderline: true,
                          }}
                          inputProps={{
                            style: {
                              color: whiteColor,
                              padding: "2px 4px",
                            },
                            autoComplete: "off",
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <Box className={classes.scenarioLabelBox}>
                          <Box
                            title={
                              localScenarioBLabel ||
                              t("comparative.comparison.scenario_b_default")
                            }
                            style={{
                              color: whiteColor,
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {localScenarioBLabel ||
                              t("comparative.comparison.scenario_b_default")}
                          </Box>
                          <EditIcon
                            className={classes.editIcon}
                            onClick={() => handleEditScenarioLabel("B")}
                            titleAccess={t(
                              "comparative.comparison.edit_label_tooltip",
                            )}
                            style={{ color: whiteColor, flexShrink: 0 }}
                          />
                        </Box>
                        <Box
                          title={selectedComparison.scenario_b?.scenario_name}
                          style={{
                            fontSize: "11px",
                            color: "#000000",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "150px",
                          }}
                        >
                          {selectedComparison.scenario_b?.scenario_name}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </>
            )}

            <NodeVisibilityControls
              nodeStructure={nodeStructure}
              visibleNodes={visibleNodes}
              onNodeVisibilityChange={handleNodeVisibilityChange}
              clientColor={clientColor}
            />
            {metadata && Object.keys(nodeStructure).length > 0 && (
              <ColumnVisibilityControls
                visibleColumns={visibleColumns}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                nodeStructure={nodeStructure}
                metadata={metadata}
                columnOrganization={columnOrganization}
                visibleNodes={visibleNodes}
              />
            )}
          </div>
          <div className={classes.rightControls}>
            <ColumnOrganizationToggle
              columnOrganization={columnOrganization}
              onToggleChange={handleColumnOrganizationToggle}
              clientColor={clientColor}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={classes.mainContent}>
        {/* Hierarchy Controls - Only show when not in initial loading */}
        {!isFullyLoading && (
          <div style={{ position: "relative" }}>
            <HierarchyControls
              hierarchyOrder={hierarchyOrder}
              availableDimensions={dimensionsNotInHierarchy}
              dimensionNames={dimensionDisplayNames}
              draggedItem={draggedItem}
              onAddDimension={handleAddDimension}
              onRemoveDimension={handleRemoveDimension}
              onClearAll={handleClearAll}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              isLoading={isLoadingDimensions}
            />
            {isLoadingDimensions && (
              <div className={classes.hierarchyLoadingOverlay}>
                <ThreeDots
                  height="40"
                  width="40"
                  radius="9"
                  color={clientColor}
                  ariaLabel="loading-dimensions"
                  visible={true}
                />
              </div>
            )}
          </div>
        )}

        {/* Scenario Tables */}
        <div className={classes.pnlSection} style={{ position: "relative" }}>
          {!selectedComparison ||
          !hierarchyOrder ||
          hierarchyOrder.length === 0 ||
          isFullyLoading ? (
            <div className={classes.placeholderContent}>
              {/* Skeleton Table Structure */}
              <div className={classes.skeletonTable}>
                {/* Header Row */}
                <div className={classes.skeletonHeaderRow}>
                  <div className={classes.skeletonHierarchyColumn}></div>
                  <div className={classes.skeletonHeaderBlock}></div>
                </div>

                {/* Main Content Area */}
                <div className={classes.skeletonMainContent}>
                  <div className={classes.skeletonHierarchyColumn}></div>
                  <div className={classes.skeletonLeftSection}></div>
                  <div className={classes.skeletonRightSection}></div>
                </div>
              </div>

              {/* Comparison Setup Card or Loading Overlay */}
              {isFullyLoading ? (
                <div className={classes.tableLoadingOverlay}>
                  <ThreeDots
                    height="50"
                    width="50"
                    radius="9"
                    color={clientColor}
                    ariaLabel="loading-comparison-data"
                    visible={true}
                  />
                  <span>{t("comparative.comparison.loading_data")}</span>
                </div>
              ) : (
                <div className={classes.placeholderOverlay}>
                  <ComparisonSetupCard
                    onComparisonSelect={onComparisonSelect}
                    scenarios={scenarios}
                  />
                </div>
              )}
            </div>
          ) : isLoadingData ? (
            <div className={classes.placeholderContent}>
              {/* Skeleton Table Structure */}
              <div className={classes.skeletonTable}>
                {/* Header Row */}
                <div className={classes.skeletonHeaderRow}>
                  <div className={classes.skeletonHierarchyColumn}></div>
                  <div className={classes.skeletonHeaderBlock}></div>
                </div>

                {/* Main Content Area */}
                <div className={classes.skeletonMainContent}>
                  <div className={classes.skeletonHierarchyColumn}></div>
                  <div className={classes.skeletonLeftSection}></div>
                  <div className={classes.skeletonRightSection}></div>
                </div>
              </div>

              {/* Loading Overlay */}
              <div className={classes.tableLoadingOverlay}>
                <ThreeDots
                  height="50"
                  width="50"
                  radius="9"
                  color={clientColor}
                  ariaLabel="loading-price-route-data"
                  visible={true}
                />
              </div>
            </div>
          ) : (
            <ScenarioTable
              selectedScenarios={[
                {
                  scenario_id: "A",
                  scenario_name:
                    localScenarioALabel ||
                    t("comparative.comparison.scenario_a_default"),
                },
                {
                  scenario_id: "B",
                  scenario_name:
                    localScenarioBLabel ||
                    t("comparative.comparison.scenario_b_default"),
                },
              ]}
              hierarchyOrder={hierarchyOrder}
              visibleNodes={visibleNodes}
              visibleColumns={visibleColumns}
              columnOrganization={columnOrganization}
              nodeStructure={nodeStructure}
              metadata={metadata}
              getScenarioData={getScenarioData}
              shouldItemBeVisible={shouldItemBeVisible}
              handleDrillDownClick={handleDrillDownClick}
              drillDownsOpen={drillDownsOpen}
              handleExpandAll={handleExpandAll}
              handleCollapseAll={handleCollapseAll}
              formatNumber={formatNumber}
            />
          )}
        </div>
      </div>
    </div>
  );
};

ComparativeTableView.propTypes = {
  selectedComparison: PropTypes.object,
  onComparisonSelect: PropTypes.func,
  selectedFilters: PropTypes.object,
  scenarios: PropTypes.array,
};

export default ComparativeTableView;
